import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { capitalize } from "@mui/material";
import { SendPostRequest } from "../../http";
import FooterWrapper from "../Footer/FooterWrapper";
import HeaderWrapper from "../Header/HeaderWrapper";
import TextMainBannerWrapper from "../TextMainBanners/TextMainBannerWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function FirstContactus() {
  const [contactData, setContactData] = useState([]);
  const sitedata = useSelector((store) => store.global.loginData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const [contactForm, setContactForm] = useState({
    fullname: "",
    email: "",
    phone: "",
    message: "",
  });

  // ✅ Email validation
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // ✅ Phone validation (only digits 10–14 length)
  const validatePhone = (phone) => {
    const regex = /^[0-9]{10,14}$/;
    return regex.test(phone);
  };

  // Function to extract src from iframe HTML
  const extractMapSrc = (iframeHtml) => {
    if (!iframeHtml) return null;
    const srcMatch = iframeHtml.match(/src="([^"]*)"/);
    return srcMatch ? srcMatch[1] : null;
  };

  // Function to open Google Map in new tab
  const openGoogleMap = () => {
    if (contactData && contactData.length > 0 && contactData[0].google_map) {
      const mapSrc = extractMapSrc(contactData[0].google_map);
      if (mapSrc) {
        window.open(mapSrc, '_blank');
      } else {
        // Fallback to address search if iframe src not found
        if (sitedata?.site_address) {
          const encodedAddress = encodeURIComponent(sitedata.site_address);
          window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.fullname) {
      toast.error("Please enter your full name");
      return;
    } else if (!contactForm.email) {
      toast.error("Please enter your email");
      return;
    } else if (!validateEmail(contactForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    } else if (!contactForm.phone) {
      toast.error("Please enter your phone number");
      return;
    } else if (!validatePhone(contactForm.phone)) {
      toast.error("Please enter a valid phone number (10-14 digits)");
      return;
    } else if (contactForm?.message === "") {
      toast.error("Please enter your message");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await SendPostRequest("contact-us-form", contactForm);
      formRef.current.reset();
      if (response.status === "success") {
        setContactForm({
          fullname: "",
          email: "",
          phone: "",
          message: "",
        });
        toast.success(response?.message);
      } else {
        let errorMessage = response?.message || "An error occurred";
        toast.error(`Error: ${errorMessage}`);
        console.error("Error fetching data:", errorMessage);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      console.error("Error fetching data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getContactData();
  }, []);

  const getContactData = async () => {
    const response = await SendPostRequest("content/contactus");
    setContactData(response?.data);
  };

  return (
    <div className="page-wrapper-inner">
      <HeaderWrapper />
      <TextMainBannerWrapper Heading="Contact Us" Image="theme-2-banner-contactus.webp"/>

      <div id="section-contact" className="section-contact form-with-img py-5 mt-3">
        <div className="container">
          <div className="row">
            {/* Contact Info */}
            <div className="col-lg-6 ps-lg-5">
              <div className="contact-info info-2 contact-style1-rapper">
                {/* Location */}
                <div className="feature-box-wrap f-list-4">
                  <div className="feature-box-details">
                    <div className="media">
                      <div className="feature-list-icon me-3">
                        <span className="ti-location-pin"></span>
                      </div>
                      <div className="media-body">
                        <div className="feature-content">
                          <div className="feature-title">
                            <h5>Our Location</h5>
                          </div>
                          <div className="feature-box-content">
                            {sitedata?.site_address ? (
                              <p className="text-black">
                                <span
                                  style={{cursor: 'pointer', textDecoration: 'none'}}
                                  onClick={openGoogleMap}
                                >
                                  {sitedata?.site_address}
                                </span>
                              </p>
                            ) : (
                              <p className="text-black">--</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="feature-box-wrap f-list-4">
                  <div className="feature-box-details">
                    <div className="media">
                      <div className="feature-list-icon me-3">
                        <span className="ti-headphone-alt"></span>
                      </div>
                      <div className="media-body">
                        <div className="feature-content">
                          <div className="feature-title">
                            <h5>Phone Number</h5>
                          </div>
                          <div className="feature-box-content">
                            <p>
                              <a
                                className="text-black"
                                href={`tel:${sitedata?.site_contact}`}
                              >
                                {sitedata?.site_contact || "--"}
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="feature-box-wrap f-list-4">
                  <div className="feature-box-details">
                    <div className="media">
                      <div className="feature-list-icon me-3">
                        <span className="ti-email"></span>
                      </div>
                      <div className="media-body">
                        <div className="feature-content">
                          <div className="feature-title">
                            <h5>Email Address</h5>
                          </div>
                          <div className="feature-box-content">
                            <p className="text-black">
                              <a
                                className="text-black"
                                href={`mailto:${sitedata?.business_email || sitedata?.site_email}`}
                              >
                                {sitedata?.business_email || sitedata?.site_email || "--"}
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-6 align-self-center">
              <div className="contact-form-4">
                <div className="contact-form-wrap">
                  <form ref={formRef} onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <div className="form-group">
                          <input
                            id="name"
                            className="form-control"
                            name="fullname"
                            placeholder="Name"
                            type="text"
                            value={contactForm.fullname}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-md-12 mb-3">
                        <div className="form-group">
                          <input
                            id="email"
                            className="form-control"
                            name="email"
                            placeholder="Email"
                            type="text" 
                            value={contactForm.email}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-md-12 mb-3">
                        <div className="form-group">
                          <input
                            id="phone"
                            className="form-control"
                            name="phone"
                            placeholder="Phone"
                            type="text"
                            value={contactForm.phone}
                            onChange={handleInputChange}
                            maxLength={14}
                            minLength={10}
                          />
                        </div>
                      </div>

                      <div className="col-md-12 mb-3">
                        <div className="contact-message">
                          <div className="form-group">
                            <textarea
                              id="message"
                              className="form-control textarea"
                              rows="5"
                              name="message"
                              placeholder="Your Message"
                              value={contactForm.message}
                              onChange={handleInputChange}
                            ></textarea>
                          </div>
                        </div>
                        <button
                          id="contact-submit"
                          className="btn btn-default mt-3 theme-btn"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Send Now"}
                        </button>
                      </div>
                    </div>
                    <span className="clearfix"></span>
                  </form>
                  <p id="contact-status-msg" className="hide"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FooterWrapper />

      {/* ✅ Toast Container added here */}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default FirstContactus;